import clamp from 'lodash/clamp';

type ScrollAxis = 'scrollLeft' | 'scrollTop';

interface SpringState {
  position: number;
  velocity: number;
  target: number;
  time: number;
  frame: number | null;
}

const SCROLL_AXES: Array<ScrollAxis> = ['scrollLeft', 'scrollTop'];

const ANGULAR_FREQUENCY = (2 * Math.PI) / 0.3;

const REST_DISTANCE = 0.5;

const REST_VELOCITY = 10;

const springs = new WeakMap<HTMLElement, Map<ScrollAxis, SpringState>>();

const createIdleState = (position: number): SpringState => ({
  position,
  velocity: 0,
  target: position,
  time: 0,
  frame: null,
});

interface GetSpringProps {
  element: HTMLElement;
  axis: ScrollAxis;
}

const getSpring = ({ element, axis }: GetSpringProps) => springs.get(element)?.get(axis);

interface SetSpringProps {
  element: HTMLElement;
  axis: ScrollAxis;
  state: SpringState;
}

const setSpring = ({ element, axis, state }: SetSpringProps) => {
  springs.set(element, (springs.get(element) ?? new Map<ScrollAxis, SpringState>()).set(axis, state));
};

interface StepSpringProps {
  state: SpringState;
  elapsed: number;
}

const stepSpring = ({ state, elapsed }: StepSpringProps) => {
  const offset = state.position - state.target;
  const slope = state.velocity + ANGULAR_FREQUENCY * offset;
  const decay = Math.exp(-ANGULAR_FREQUENCY * elapsed);
  const remaining = offset + slope * elapsed;

  return {
    position: state.target + remaining * decay,
    velocity: (slope - ANGULAR_FREQUENCY * remaining) * decay,
  };
};

interface TickProps {
  element: HTMLElement;
  axis: ScrollAxis;
}

const tick =
  ({ element, axis }: TickProps) =>
  (time: number) => {
    const state = getSpring({ element, axis });

    if (!state) {
      return;
    }

    const { position, velocity } = stepSpring({ state, elapsed: Math.max(0, (time - state.time) / 1000) });
    const settled = Math.abs(position - state.target) < REST_DISTANCE && Math.abs(velocity) < REST_VELOCITY;

    element[axis] = settled ? state.target : position;
    setSpring({
      element,
      axis,
      state: settled
        ? createIdleState(state.target)
        : { ...state, position, velocity, time, frame: requestAnimationFrame(tick({ element, axis })) },
    });
  };

const syncIdleSprings = ({ target }: Event) => {
  if (!(target instanceof HTMLElement)) {
    return;
  }

  SCROLL_AXES.forEach((axis) => {
    const state = getSpring({ element: target, axis });

    if (!state || state.frame === null) {
      setSpring({ element: target, axis, state: createIdleState(target[axis]) });
    }
  });
};

document.addEventListener('scroll', syncIdleSprings, { capture: true, passive: true });

interface GetMaxScrollProps {
  element: HTMLElement;
  axis: ScrollAxis;
}

const getMaxScroll = ({ element, axis }: GetMaxScrollProps) =>
  Math.max(
    0,
    axis === 'scrollLeft' ? element.scrollWidth - element.clientWidth : element.scrollHeight - element.clientHeight,
  );

interface ScrollAxisToProps {
  element: HTMLElement;
  axis: ScrollAxis;
  value: number;
  behavior?: ScrollBehavior;
}

const scrollAxisTo = ({ element, axis, value, behavior }: ScrollAxisToProps) => {
  const target = clamp(value, 0, getMaxScroll({ element, axis }));
  const current = getSpring({ element, axis }) ?? createIdleState(0);

  if (behavior === 'auto') {
    if (current.frame !== null) {
      cancelAnimationFrame(current.frame);
    }

    element[axis] = target;
    setSpring({ element, axis, state: createIdleState(target) });

    return;
  }

  setSpring({
    element,
    axis,
    state:
      current.frame === null
        ? { ...current, target, time: performance.now(), frame: requestAnimationFrame(tick({ element, axis })) }
        : { ...current, target },
  });
};

interface ScrollElementToProps {
  element: HTMLElement;
  left?: number;
  top?: number;
  behavior?: ScrollBehavior;
}

export const scrollElementTo = ({ element, left, top, behavior }: ScrollElementToProps) => {
  if (left !== undefined) {
    scrollAxisTo({ element, axis: 'scrollLeft', value: left, behavior });
  }

  if (top !== undefined) {
    scrollAxisTo({ element, axis: 'scrollTop', value: top, behavior });
  }
};
